<template>
  <main role="main" class="py-4">
    <b-container>
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("newAccountsDay") }}</h4>
        <div>
          <!-- <b-button variant="outline-secondary" @click="onClickReloadBtn">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button> -->
        </div>
      </div>

      <b-form class="d-flex flex-row align-items-center mb-3" @submit.prevent="onClickSearchBtn">
        <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
          <div class="flex-shrink-0 mr-3"> {{ $t("fromDate") }}</div>
          <b-form-datepicker v-model="startDate" value-as-date required
                             :max="endDate ? endDate: new Date()"
                             no-flip hide-header
                             label-no-date-selected="DD/MM/YYYY"
                             locale="vi"
                             :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                             start-weekday="1"></b-form-datepicker>
        </div>
        <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
          <div class="flex-shrink-0 mr-3">{{ $t("toDate") }}</div>
          <b-form-datepicker v-model="endDate" value-as-date required
                             :min= "startDate ? startDate: new Date()"
                             no-flip hide-header
                             label-no-date-selected="DD/MM/YYYY"
                             locale="vi"
                             :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                             start-weekday="1"></b-form-datepicker>
        </div>
        <b-button type="submit" variant="primary">{{ $t("apply") }}</b-button>
      </b-form>

      <b-table ref="table" :items="tableItemProvider" hover primary-key="id" :fields="tableFields" :busy="isLoading" show-empty
               :per-page="itemsPerPage" :current-page="currentPage" 
               sort-by="createdAt"
               :sort-desc="true">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        
      </b-table>
      <div class="d-flex flex-row align-items-center justify-content-between">
        <div></div>
        <!-- <b-button variant="link" :disabled="!totalItems || isExportingToExcel" @click="onClickExportToExcel">
          <b-icon-download class="mr-2"></b-icon-download> {{ $t("export") }}
        </b-button> -->
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>
    </b-container>
  </main>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import moment from "moment";
  import BaseComponent from "@/components/BaseComponent";
  import {BvTableFieldArray} from "bootstrap-vue";
  import {BTable, BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";
  import * as reportService from "@/service/reportService";
  import * as areaService from "@/service/areaService";
import { formatDateRange } from '@/service/util';

  @Component
  export default class NewUsersDay extends BaseComponent {

    searchText = "";

    searchAreaId: number = null;
    searchAreaOptions = [];

    startDate: Date = null;
    endDate: Date = null;
    formErrorMsg = "";
    isLoading = false;

    @Ref("table") table: BTable;
    sortBy= 'name';
    sortDesc= true;
    tableFields: BvTableFieldArray = [
    {
      key: "id",
      label: "ID",
      thClass: "text-nowrap",
    },
    {
      key: "name",
      label: this.$t("fullName").toString(),
      thClass: "text-nowrap",
      tdClass: "text-break",
    },
    {
      key: "username",
      label: "Username",
      thClass: "text-nowrap",
      tdClass: "text-break",
    },
    {
      key: "phone",
      label: this.$t("phoneNumber").toString(),
      thClass: "text-nowrap",
    },
    {
      key: "email",
      label: "Email",
      thClass: "text-nowrap",
      tdClass: "text-break",
    },
    {
      key: "role",
      label: this.$t("role").toString(),
      formatter: value => ({ Customer: this.$t("roleAdmin").toString(), User: this.$t("roleUser").toString() }[value] || value),
    },
    {
      key: "area",
      label: this.$t("saleArea").toString(),
      thClass: "text-nowrap",
      tdClass: "text-break",
    },
    {
      key: "customerName",
      label: this.$t("customer").toString(),
      thClass: "text-nowrap",
      tdClass: "text-break",
    },
    {
      key: "createdAt",
      label: this.$t("createdDate").toString(),
    }
    ];

    isExportingToExcel = false;

    itemsPerPageSelections = [
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
      { value: 200, text: "200 " + this.$t("itemsPerPage").toString() },
    ];
    itemsPerPage = 20;
    currentPage = 1;
    totalItems = 0;


    mounted() {
      this.startDate = moment().subtract(0, "days").startOf("day").toDate();
      this.endDate = moment().endOf("day").toDate();
      this.fetchAreas();
    }

    async fetchAreas() {
      try {
        const areas = await areaService.getAll();
        this.searchAreaOptions = areas.map(a => ({ value: a.id, text: a.name }));
        this.searchAreaOptions.unshift({ value: null, text: this.$t("all").toString()});

      } catch (e) {
        this.toastError(e);
      }
    }

    onClickReloadBtn() {
      this.searchText = "";
      this.startDate = moment().subtract(0, "days").startOf("day").toDate();
      this.endDate = moment().endOf("day").toDate();
      this.formErrorMsg = "";
      this.itemsPerPage = 20;
      this.currentPage = 1;
      this.totalItems = 0;

      this.table.refresh();
    }

    onClickSearchBtn() {
      this.table.refresh();
    }

    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
      try {
        const { fromDate, toDate } = formatDateRange(this.startDate, this.endDate);
        const customerListRes = await reportService.getAccountCreateList(
          fromDate,
          toDate,
          ctx.currentPage,
          this.itemsPerPage
        );

        this.totalItems = customerListRes.total;
        
        return customerListRes.data;

      } catch (e) {
        this.toastError(e);
        return [];
      }
    }


}
</script>
